import { Contact } from "../../core/types";
import { Page } from "../../types/page";

// Storage access itself can throw (not just JSON parsing) — sessionStorage /
// localStorage raise SecurityError when cookies/storage are disabled, in some
// sandboxed iframe contexts, or when quota is exceeded. Wrap every direct
// access so callers never crash on the read/write path.
const safeGetItem = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (storage: Storage, key: string, value: string): void => {
  try {
    storage.setItem(key, value);
  } catch {
    /* storage unavailable — best-effort persistence */
  }
};

const safeRemoveItem = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

// Session id and UTM are intentionally page-level (not placement-scoped): multiple
// placements on one page share one analytics session and one UTM attribution for the
// visit. Page routing IS placement-scoped (see below) — each placement restores its own
// last page independently.
const setSessionId = (sessionId: string): void => {
  safeSetItem(sessionStorage, "LastbotChat_sessionId", sessionId);
};

const getSessionId = (): string => {
  return safeGetItem(sessionStorage, "LastbotChat_sessionId") || "";
};

/**
 * Scope for placement-local persisted UI state (current page, login-return page).
 * Keys combine the widget id with the optional placement id
 * (`<widgetId>:<encodeURIComponent(placementId)>`, or `<widgetId>` alone), so two
 * placements of one widget — or two different widgets sharing a placement-id
 * string — never collide on one storage entry. The placement-id half is
 * URI-encoded so an embedder-chosen id containing `:` cannot alias another
 * scope; widget ids are server-issued UUIDs and never contain `:`. Distinct
 * from the placement-REGISTRY key (`placementId || widgetId`) used for
 * postMessage targeting and `getCore()`.
 */
export interface PageStorageScope {
  widgetId: string;
  placementId?: string;
}

const scopedStorageKey = ({ widgetId, placementId }: PageStorageScope): string =>
  placementId ? `${widgetId}:${encodeURIComponent(placementId)}` : widgetId;

// Read a scoped sessionStorage entry with two legacy fallbacks so sessions
// already in flight when a new version deploys keep their stored page:
//   1. `<baseKey>:<widgetId>:<encodedPlacementId>` — current scheme, authoritative
//   2. `<baseKey>:<placementId || widgetId>`       — previous placement-key scheme (raw)
//   3. `<baseKey>`                                  — pre-scoping bare key
// The composite key becomes authoritative on the next write.
const getScopedWithLegacyFallback = (baseKey: string, scope: PageStorageScope): string | null => {
  return (
    safeGetItem(sessionStorage, `${baseKey}:${scopedStorageKey(scope)}`) ??
    safeGetItem(sessionStorage, `${baseKey}:${scope.placementId || scope.widgetId}`) ??
    safeGetItem(sessionStorage, baseKey)
  );
};

const setPage = (scope: PageStorageScope, page: Page): void => {
  safeSetItem(sessionStorage, `LastbotChat_page:${scopedStorageKey(scope)}`, JSON.stringify(page));
};

const getPage = (scope: PageStorageScope): Page => {
  const page = getScopedWithLegacyFallback("LastbotChat_page", scope);
  if (!page) return { type: "landing" };
  try {
    return JSON.parse(page);
  } catch {
    console.warn("[lastbot-chat] sessionStorage 'page' is not valid JSON; resetting");
    return { type: "landing" };
  }
};

const setPageBeforeLogin = (scope: PageStorageScope, page: Page): void => {
  safeSetItem(
    sessionStorage,
    `LastbotChat_pageBeforeLogin:${scopedStorageKey(scope)}`,
    JSON.stringify(page),
  );
};

const getPageBeforeLogin = (scope: PageStorageScope): Page => {
  const page = getScopedWithLegacyFallback("LastbotChat_pageBeforeLogin", scope);
  if (!page) return { type: "landing" };
  try {
    return JSON.parse(page);
  } catch {
    console.warn("[lastbot-chat] sessionStorage 'pageBeforeLogin' is not valid JSON; resetting");
    return { type: "landing" };
  }
};

const setContact = (widgetId: string, contact: Contact | null): void => {
  if (contact) {
    safeSetItem(localStorage, `LastbotChat_contact:${widgetId}`, JSON.stringify(contact));
  } else {
    safeRemoveItem(localStorage, `LastbotChat_contact:${widgetId}`);
  }
};

const getContact = (widgetId: string, attribute: keyof Contact): string | boolean => {
  const contact = safeGetItem(localStorage, `LastbotChat_contact:${widgetId}`);
  if (!contact) return "";
  try {
    const parsed = JSON.parse(contact) as Partial<Contact> | null;
    const value = parsed && typeof parsed === "object" ? parsed[attribute] : undefined;
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value;
    return "";
  } catch {
    console.warn(
      `[lastbot-chat] localStorage 'LastbotChat_contact:${widgetId}' is not valid JSON; treating as missing`,
    );
    return "";
  }
};

// Whether the visitor closed this placement during the current browser session.
// Auto-open consults it so a close holds across page loads on the same site
// (LAS-4555). sessionStorage on purpose: the flag records a choice the visitor
// made in this tab and disappears with the tab, which is the least storage that
// does the job. Unavailable storage reads as "not dismissed", i.e. today's
// auto-open behaviour.
const setDismissed = (scope: PageStorageScope, dismissed: boolean): void => {
  const key = `LastbotChat_dismissed:${scopedStorageKey(scope)}`;
  // The `sessionStorage` getter itself throws when storage is blocked, before
  // the safe helpers' try block runs. This runs inside the close handler, so a
  // throw here would stop the panel from closing.
  try {
    if (dismissed) {
      safeSetItem(sessionStorage, key, "1");
    } else {
      safeRemoveItem(sessionStorage, key);
    }
  } catch {
    /* storage unavailable — the close is simply not remembered */
  }
};

const isDismissed = (scope: PageStorageScope): boolean => {
  try {
    return safeGetItem(sessionStorage, `LastbotChat_dismissed:${scopedStorageKey(scope)}`) === "1";
  } catch {
    return false;
  }
};

const setUtmParams = (utmParams: Record<string, string>): void => {
  safeSetItem(sessionStorage, "LastbotChat_utm", JSON.stringify(utmParams));
};

const getUtmParams = (): Record<string, string> => {
  const utmParams = safeGetItem(sessionStorage, "LastbotChat_utm");
  if (!utmParams) return {};
  try {
    const parsed = JSON.parse(utmParams) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
    return {};
  } catch {
    console.warn("[lastbot-chat] sessionStorage 'utm' is not valid JSON; resetting");
    return {};
  }
};

export default {
  setSessionId,
  getSessionId,
  setPage,
  getPage,
  setPageBeforeLogin,
  getPageBeforeLogin,
  setContact,
  getContact,
  setUtmParams,
  getUtmParams,
  setDismissed,
  isDismissed,
};
